// Layer 2 (Engine): SPC over B5 series — limits from baseline only (D8).
// See docs/engines/spc.md.

import type {
  ControlLimitSet,
  ExcursionEvent,
  ExcursionRuleKind,
  ExcursionSeverity,
  MonitoredVariable,
  SignalState,
  SpcPhase,
  SpcRead,
} from '@/versions/v1/schema/spc';
import type { Band, Reading } from '@/versions/v1/schema/reading';
import type { SeriesTime } from '@/versions/v1/schema/reading-series';
import type { SessionState } from '@/versions/v1/schema/scenario';
import { MONITORED_VARIABLES } from '@/versions/v1/shared/bunker-hill-spc';
import { READINGS } from '@/versions/v1/shared/bunker-hill-readings';
import { SERIES_BY_ID } from '@/versions/v1/shared/bunker-hill-series';
import { formatBand } from '@/versions/v1/shared/reading.derive';
import { seriesAt } from '@/versions/v1/shared/series.derive';

/** Rev. 2: UCL/LCL = mean ± 3·σ (σ = max(mrSigma, s)). */
const SIGMA_MR_FACTOR = 1.128;
const SIGMA_LIMIT_MULTIPLIER = 3;
/** MR chart: mrUCL = 3.267·mrMean. */
const MR_UCL_FACTOR = 3.267;
/** Trend threshold in σ per point (docs/engines/spc.md §5). */
const TREND_SIGMA_THRESHOLD = 0.15;
/** Cyclical rule minimum run length. */
const CYCLICAL_MIN_POINTS = 8;
/** Cpk at or above this is capable (construction plan § 7.7). */
const CAPABLE_CPK_THRESHOLD = 1;

const VARIABLE_BY_ID = new Map(MONITORED_VARIABLES.map((v) => [v.id, v]));
const READING_BY_ID = new Map(READINGS.map((r) => [r.id, r]));

function specForVariable(variable: MonitoredVariable): Band | undefined {
  const primary = READING_BY_ID.get(variable.readingId);
  if (primary?.spec) return primary.spec;
  const series = SERIES_BY_ID[variable.seriesId];
  if (!series) return undefined;
  for (const rid of series.readingIds) {
    const row = READING_BY_ID.get(rid);
    if (row?.spec) return row.spec;
  }
  return undefined;
}

function designForForecast(variable: MonitoredVariable): number | undefined {
  const primary = READING_BY_ID.get(variable.readingId);
  if (primary?.design !== undefined) return primary.design;
  for (const rid of SERIES_BY_ID[variable.seriesId]?.readingIds ?? []) {
    const row = READING_BY_ID.get(rid);
    if (row?.design !== undefined) return row.design;
  }
  return undefined;
}

const SEVERITY_RANK: Record<ExcursionSeverity, number> = { strong: 2, signal: 1 };

export type StabilityResult = {
  phase: SpcPhase;
  limits?: ControlLimitSet;
  points: SpcRead['points'];
  events: ExcursionEvent[];
  trend: SpcRead['trend'];
};

export type ConformanceResult = {
  bandPosition: 'inside' | 'below' | 'above' | 'n/a';
  /** Latest point inside spec — on-bound is conforming (strict `>` / `<` for breach). */
  conforming?: boolean;
  pointsToSpec?: number;
  boundUsed?: ExcursionEvent['boundUsed'];
};

/** SPC chart decimals: reading.decimals when set, else 1 — never precisionFromNumber on computed values. */
export function spcDisplayDecimals(reading: Reading): number {
  return reading.decimals ?? 1;
}

export function formatSpcValue(reading: Reading, value: number): string {
  const d = spcDisplayDecimals(reading);
  return value.toLocaleString('en-US', {
    minimumFractionDigits: d,
    maximumFractionDigits: d,
  });
}

/** Cpk is dimensionless — always one decimal regardless of the reading's decimals. */
export function formatCpkValue(cpk: number): string {
  return cpk.toLocaleString('en-US', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });
}

export type ControlChartCaptionOptions = {
  /** When false, omit Cpk (row label already carries it). Default true. */
  includeCpk?: boolean;
  /** When true, carry spec bound and σ source instead of Cpk (not-capable drift rows). */
  showSpecBasis?: boolean;
};

export type ControlChartCaption = {
  mean: string;
  ucl: string;
  lcl: string;
  sigma: string;
  rule?: string;
  conforming?: 'conforming' | 'non-conforming';
  cpk?: string;
  specBound?: string;
  sigmaSource?: 'mr' | 'sample';
};

export type ControlChartViewModel = {
  variableId: string;
  readingId: string;
  readingLabel: string;
  phase: SpcPhase;
  points: readonly { at: SeriesTime; value: number; mr?: number; highlighted?: boolean }[];
  limits?: ControlLimitSet;
  spec?: Band;
  specLabel?: string;
  events: readonly ExcursionEvent[];
  trend: SpcRead['trend'];
  capability?: SpcRead['capability'];
  signal?: SignalState;
  learningCaption?: string;
  caption?: ControlChartCaption;
};

/** Band position — on-bound counts as inside (same convention as spec conformance). */
export function valueBandPosition(
  value: number | undefined,
  band: Band | undefined,
): ConformanceResult['bandPosition'] {
  if (value === undefined || !band) return 'n/a';
  if (band.lo !== undefined && value < band.lo) return 'below';
  if (band.hi !== undefined && value > band.hi) return 'above';
  return 'inside';
}

/** Spec conformance — on-bound is conforming; breach uses strict `>` / `<`. */
export function valueConformance(
  value: number | undefined,
  spec: Band | undefined,
): boolean | undefined {
  if (value === undefined || !spec) return undefined;
  if (spec.hi !== undefined && value > spec.hi) return false;
  if (spec.lo !== undefined && value < spec.lo) return false;
  return true;
}

function numericPoints(
  variable: MonitoredVariable,
  state: SessionState,
): { at: SeriesTime; value: number }[] {
  const view = seriesAt(variable.seriesId, state);
  return view.points.filter((p): p is { at: SeriesTime; value: number } => p.value !== null);
}

function mean(values: readonly number[]): number {
  if (values.length === 0) return 0;
  return values.reduce((s, v) => s + v, 0) / values.length;
}

function sampleStdDev(values: readonly number[]): number {
  if (values.length < 2) return 0;
  const m = mean(values);
  const variance = values.reduce((s, v) => s + (v - m) ** 2, 0) / (values.length - 1);
  return Math.sqrt(variance);
}

function movingRanges(values: readonly number[]): number[] {
  const mrs: number[] = [];
  for (let i = 1; i < values.length; i++) {
    mrs.push(Math.abs(values[i]! - values[i - 1]!));
  }
  return mrs;
}

function leastSquaresSlope(xs: readonly number[], ys: readonly number[]): number {
  const n = xs.length;
  if (n < 2) return 0;
  const xMean = mean(xs);
  const yMean = mean(ys);
  let num = 0;
  let den = 0;
  for (let i = 0; i < n; i++) {
    const dx = xs[i]! - xMean;
    num += dx * (ys[i]! - yMean);
    den += dx * dx;
  }
  return den === 0 ? 0 : num / den;
}

function computeLimits(
  variableId: string,
  baselinePoints: readonly { at: SeriesTime; value: number }[],
): ControlLimitSet {
  const values = baselinePoints.map((p) => p.value);
  const xbar = mean(values);
  const mrs = movingRanges(values);
  const mrMean = mrs.length > 0 ? mean(mrs) : 0;
  const mrSigma = mrMean / SIGMA_MR_FACTOR;
  const s = sampleStdDev(values);
  const sigma = Math.max(mrSigma, s);
  const sigmaSource = s > mrSigma ? 'sample' : 'mr';
  const half = SIGMA_LIMIT_MULTIPLIER * sigma;
  return {
    variableId,
    mean: xbar,
    ucl: xbar + half,
    lcl: xbar - half,
    mrMean,
    mrUcl: MR_UCL_FACTOR * mrMean,
    sigma,
    baseline: {
      from: baselinePoints[0]!.at,
      to: baselinePoints[baselinePoints.length - 1]!.at,
      n: baselinePoints.length,
    },
    sigmaSource,
  };
}

function trendFromTestSpan(
  testValues: readonly number[],
  sigma: number,
): SpcRead['trend'] {
  if (testValues.length < 2 || sigma === 0) return 'stable';
  const xs = testValues.map((_, i) => i);
  const slopePerPoint = leastSquaresSlope(xs, testValues);
  const slopeSigma = slopePerPoint / sigma;
  if (slopeSigma > TREND_SIGMA_THRESHOLD) return 'rising';
  if (slopeSigma < -TREND_SIGMA_THRESHOLD) return 'falling';
  return 'stable';
}

function specBoundForDirection(
  spec: Band | undefined,
  direction: 'up' | 'down',
): number | undefined {
  if (!spec) return undefined;
  if (direction === 'up') return spec.hi;
  return spec.lo;
}

function signalCell(
  control: SignalState['control'],
  conformance: SignalState['conformance'],
  capable: boolean | undefined,
): SignalCell {
  if (control === 'in') {
    if (conformance === 'non-conforming' || capable === false) return 'not-capable';
    return 'quiet';
  }
  if (conformance === 'non-conforming') return 'consequence';
  return 'drift';
}

type SignalCell = SignalState['cell'];

function buildSignalState(
  events: readonly ExcursionEvent[],
  conforming: boolean | undefined,
  capability?: SpcRead['capability'],
  spec?: Band,
): SignalState {
  const control: SignalState['control'] = events.length > 0 ? 'out' : 'in';
  const conformance: SignalState['conformance'] =
    spec === undefined
      ? 'unknown'
      : conforming === false
        ? 'non-conforming'
        : 'conforming';
  const capable =
    spec === undefined || capability?.cpk === undefined
      ? undefined
      : capability.cpk >= CAPABLE_CPK_THRESHOLD;
  return {
    control,
    conformance,
    capable,
    cell: signalCell(control, conformance, capable),
  };
}

function eventSignalState(
  conforming: boolean | undefined,
  spec: Band | undefined,
  limits: ControlLimitSet | undefined,
  reading: Reading | undefined,
): SignalState {
  const conformance: SignalState['conformance'] =
    spec === undefined
      ? 'unknown'
      : conforming === false
        ? 'non-conforming'
        : 'conforming';
  const cap =
    reading && spec && limits ? capability({ limits }, reading, spec) : undefined;
  const capable =
    spec === undefined || cap?.cpk === undefined
      ? undefined
      : cap.cpk >= CAPABLE_CPK_THRESHOLD;
  return {
    control: 'out',
    conformance,
    capable,
    cell: conformance === 'non-conforming' ? 'consequence' : 'drift',
  };
}

function applyOverlay(
  event: Omit<ExcursionEvent, 'conforming' | 'slopePerPoint' | 'pointsToSpec' | 'boundUsed' | 'signal'>,
  variable: MonitoredVariable,
  testPoints: readonly { at: SeriesTime; value: number }[],
  limits: ControlLimitSet,
): ExcursionEvent {
  const spec = specForVariable(variable);
  const testValues = testPoints.map((p) => p.value);
  const latest = testValues[testValues.length - 1];
  const conforming = valueConformance(latest, spec);

  let slopePerPoint: number | undefined;
  let pointsToSpec: number | undefined;
  let boundUsed: ExcursionEvent['boundUsed'];

  if (event.rule === 'six-point-trend' && event.direction && event.points.length >= 2) {
    const runStart = testPoints.findIndex((p) => p.at === event.points[0]);
    const runValues =
      runStart >= 0
        ? testPoints.slice(runStart, runStart + event.points.length).map((p) => p.value)
        : event.points.map((stamp) => testPoints.find((p) => p.at === stamp)?.value ?? latest!);
    const xs = runValues.map((_, i) => i);
    slopePerPoint = leastSquaresSlope(xs, runValues);

    const specBound = specBoundForDirection(spec, event.direction);
    const designBound = designForForecast(variable);
    let bound: number | undefined;
    if (event.direction === 'down') {
      if (specBound !== undefined) {
        bound = specBound;
        boundUsed = 'spec';
      } else if (designBound !== undefined) {
        bound = designBound;
        boundUsed = 'design';
      }
    } else if (spec?.hi !== undefined) {
      bound = spec.hi;
      boundUsed = 'spec';
    } else if (designBound !== undefined) {
      bound = designBound;
      boundUsed = 'design';
    }

    if (bound !== undefined && latest !== undefined && slopePerPoint !== 0) {
      if (event.direction === 'down' && slopePerPoint < 0 && latest > bound) {
        pointsToSpec = Math.ceil((latest - bound) / Math.abs(slopePerPoint) - 1e-9);
      } else if (event.direction === 'up' && slopePerPoint > 0 && latest < bound) {
        pointsToSpec = Math.ceil((bound - latest) / slopePerPoint - 1e-9);
      }
    }
  }

  const reading = READING_BY_ID.get(variable.readingId);

  return {
    ...event,
    conforming,
    slopePerPoint,
    pointsToSpec,
    boundUsed,
    signal: eventSignalState(conforming, spec, limits, reading),
  };
}

export function capability(
  read: Pick<SpcRead, 'limits'>,
  reading: Reading,
  specOverride?: Band,
): SpcRead['capability'] | undefined {
  const spec = specOverride ?? reading.spec;
  const limits = read.limits;
  if (!spec || !limits) return undefined;
  const { mean, sigma } = limits;
  if (sigma === 0) return undefined;
  let cp: number | undefined;
  if (spec.lo !== undefined && spec.hi !== undefined) {
    cp = (spec.hi - spec.lo) / (6 * sigma);
  }
  const candidates: number[] = [];
  if (spec.hi !== undefined) candidates.push((spec.hi - mean) / (3 * sigma));
  if (spec.lo !== undefined) candidates.push((mean - spec.lo) / (3 * sigma));
  if (candidates.length === 0) return undefined;
  const cpk = Math.min(...candidates);
  return { cp, cpk };
}

export function conformance(
  read: Pick<SpcRead, 'limits' | 'points' | 'events'>,
  reading: Reading,
  specOverride?: Band,
): ConformanceResult {
  const latest = read.points[read.points.length - 1]?.value;
  const spec = specOverride ?? reading.spec;
  const trendEvent = read.events.find((event) => event.rule === 'six-point-trend');
  return {
    bandPosition: valueBandPosition(latest, reading.band),
    conforming: valueConformance(latest, spec),
    pointsToSpec: trendEvent?.pointsToSpec,
    boundUsed: trendEvent?.boundUsed,
  };
}

function eventBase(
  variable: MonitoredVariable,
  rule: ExcursionRuleKind,
  detectedAt: SeriesTime,
  points: readonly SeriesTime[],
  severity: ExcursionSeverity,
  direction?: 'up' | 'down',
): Omit<ExcursionEvent, 'conforming' | 'slopePerPoint' | 'pointsToSpec' | 'boundUsed' | 'signal'> {
  return {
    id: `ex-${variable.id}-${rule}`,
    variableId: variable.id,
    readingId: variable.readingId,
    rule,
    detectedAt,
    points,
    direction,
    severity,
  };
}

function detectBeyondLimit(
  variable: MonitoredVariable,
  testPoints: readonly { at: SeriesTime; value: number }[],
  limits: ControlLimitSet,
): ExcursionEvent | null {
  const last = testPoints[testPoints.length - 1];
  if (!last) return null;
  let direction: 'up' | 'down' | null = null;
  if (last.value > limits.ucl) direction = 'up';
  else if (last.value < limits.lcl) direction = 'down';
  else return null;

  let startIdx = testPoints.length - 1;
  while (startIdx > 0) {
    const prev = testPoints[startIdx - 1]!;
    if (direction === 'up' && prev.value > limits.ucl) startIdx--;
    else if (direction === 'down' && prev.value < limits.lcl) startIdx--;
    else break;
  }

  const run = testPoints.slice(startIdx);
  return applyOverlay(
    eventBase(
      variable,
      'beyond-limit',
      run[0]!.at,
      run.map((p) => p.at),
      'strong',
      direction,
    ),
    variable,
    testPoints,
    limits,
  );
}

function detectSixPointTrend(
  variable: MonitoredVariable,
  testPoints: readonly { at: SeriesTime; value: number }[],
  limits: ControlLimitSet,
): ExcursionEvent | null {
  const values = testPoints.map((p) => p.value);
  const n = values.length;
  if (n < 6) return null;

  for (let runLen = n; runLen >= 6; runLen--) {
    const start = n - runLen;
    const slice = values.slice(start, n);
    let rising = true;
    let falling = true;
    for (let i = 1; i < slice.length; i++) {
      if (slice[i]! <= slice[i - 1]!) rising = false;
      if (slice[i]! >= slice[i - 1]!) falling = false;
    }
    if (rising) {
      const stamps = testPoints.slice(start, n).map((p) => p.at);
      return applyOverlay(
        eventBase(variable, 'six-point-trend', stamps[0]!, stamps, 'signal', 'up'),
        variable,
        testPoints,
        limits,
      );
    }
    if (falling) {
      const stamps = testPoints.slice(start, n).map((p) => p.at);
      return applyOverlay(
        eventBase(variable, 'six-point-trend', stamps[0]!, stamps, 'signal', 'down'),
        variable,
        testPoints,
        limits,
      );
    }
  }
  return null;
}

function detectWideningVariability(
  variable: MonitoredVariable,
  testPoints: readonly { at: SeriesTime; value: number }[],
  limits: ControlLimitSet,
): ExcursionEvent | null {
  const last = testPoints[testPoints.length - 1];
  if (!last || testPoints.length < 2) return null;
  const mr = Math.abs(last.value - testPoints[testPoints.length - 2]!.value);
  if (mr <= limits.mrUcl) return null;
  return applyOverlay(
    eventBase(
      variable,
      'widening-variability',
      testPoints[testPoints.length - 2]!.at,
      [testPoints[testPoints.length - 2]!.at, last.at],
      'signal',
    ),
    variable,
    testPoints,
    limits,
  );
}

function detectCyclical(
  variable: MonitoredVariable,
  testPoints: readonly { at: SeriesTime; value: number }[],
  limits: ControlLimitSet,
): ExcursionEvent | null {
  const signs = testPoints.map((p) => Math.sign(p.value - limits.mean));
  let bestStart = -1;
  let bestLen = 0;
  for (let start = 0; start <= signs.length - CYCLICAL_MIN_POINTS; start++) {
    let len = 1;
    for (let i = start + 1; i < signs.length; i++) {
      if (signs[i] === 0 || signs[i - 1] === 0 || signs[i] === signs[i - 1]) break;
      len++;
    }
    if (len >= CYCLICAL_MIN_POINTS && len > bestLen) {
      bestLen = len;
      bestStart = start;
    }
  }
  if (bestStart < 0) return null;
  const lastIdx = testPoints.length - 1;
  const runEnd = bestStart + bestLen - 1;
  if (runEnd !== lastIdx) return null;
  const run = testPoints.slice(bestStart, bestStart + bestLen);
  return applyOverlay(
    eventBase(
      variable,
      'cyclical',
      run[0]!.at,
      run.map((p) => p.at),
      'signal',
    ),
    variable,
    testPoints,
    limits,
  );
}

const RULE_DETECTORS: Record<
  Exclude<ExcursionRuleKind, 'six-point-trend'>,
  (
    variable: MonitoredVariable,
    testPoints: readonly { at: SeriesTime; value: number }[],
    limits: ControlLimitSet,
  ) => ExcursionEvent | null
> = {
  'beyond-limit': detectBeyondLimit,
  'widening-variability': detectWideningVariability,
  cyclical: detectCyclical,
};

function splitWindows(
  allPoints: readonly { at: SeriesTime; value: number }[],
  variable: MonitoredVariable,
): {
  phase: SpcPhase;
  baseline: { at: SeriesTime; value: number }[];
  test: { at: SeriesTime; value: number }[];
} {
  const n = allPoints.length;
  if (variable.phase === 'learning') {
    return { phase: 'learning', baseline: [], test: allPoints.slice() };
  }
  if (n < variable.learningPoints) {
    return { phase: 'learning', baseline: [], test: allPoints.slice() };
  }
  const testCount = Math.min(variable.testPoints, n);
  const test = allPoints.slice(n - testCount);
  const baselineEnd = n - testCount;
  const baselineStart = Math.max(0, baselineEnd - variable.baselinePoints);
  const baseline = allPoints.slice(baselineStart, baselineEnd);
  return { phase: variable.phase, baseline, test };
}

function evaluateRules(
  variable: MonitoredVariable,
  test: readonly { at: SeriesTime; value: number }[],
  limits: ControlLimitSet,
): ExcursionEvent[] {
  const events: ExcursionEvent[] = [];
  for (const kind of Object.keys(RULE_DETECTORS) as Array<keyof typeof RULE_DETECTORS>) {
    const raw = RULE_DETECTORS[kind](variable, test, limits);
    if (raw) events.push(raw);
  }
  const trendEvent = detectSixPointTrend(variable, test, limits);
  if (trendEvent) events.push(trendEvent);
  return events;
}

export function stability(variable: MonitoredVariable, state: SessionState): StabilityResult {
  const allPoints = numericPoints(variable, state);
  const { phase, baseline, test } = splitWindows(allPoints, variable);

  if (phase === 'learning' || baseline.length < 2) {
    const chartPoints = test.map((p, i) => ({
      at: p.at,
      value: p.value,
      mr: i > 0 ? Math.abs(p.value - test[i - 1]!.value) : undefined,
    }));
    return {
      phase: 'learning',
      points: chartPoints,
      events: [],
      trend: 'stable',
    };
  }

  const limits = computeLimits(variable.id, baseline);
  const testValues = test.map((p) => p.value);
  const trend = trendFromTestSpan(testValues, limits.sigma);
  const events = evaluateRules(variable, test, limits);
  const chartPoints = test.map((p, i) => ({
    at: p.at,
    value: p.value,
    mr: i > 0 ? Math.abs(p.value - test[i - 1]!.value) : undefined,
  }));

  return { phase, limits, points: chartPoints, events, trend };
}

export function spcRead(variableId: string, state: SessionState): SpcRead {
  const variable = VARIABLE_BY_ID.get(variableId);
  if (!variable) throw new Error(`Unknown monitored variable ${variableId}`);

  const stable = stability(variable, state);
  if (stable.phase === 'learning' || !stable.limits) {
    return {
      variable,
      phase: 'learning',
      points: stable.points,
      events: [],
      trend: 'stable',
    };
  }

  const reading = READING_BY_ID.get(variable.readingId);
  const spec = specForVariable(variable);
  const cap =
    reading && spec ? capability({ limits: stable.limits }, reading, spec) : undefined;
  const conf = reading
    ? conformance(
        { limits: stable.limits, points: stable.points, events: stable.events },
        reading,
        spec,
      )
    : { bandPosition: 'n/a' as const, conforming: undefined };

  return {
    variable,
    phase: stable.phase,
    limits: stable.limits,
    points: stable.points,
    events: stable.events,
    trend: stable.trend,
    capability: cap,
    signal: buildSignalState(stable.events, conf.conforming, cap, spec),
  };
}

/** Long-window trend over the full series — σ per week (#272). */
export type LongTrendRead = {
  trend: SpcRead['trend'];
  startValue: number;
  endValue: number;
  slopeSigmaPerWeek: number;
};

const LONG_SERIES_BY_ROLE: Partial<Record<string, string>> = {
  'grind-p80': 'rs-cyc-of-p80-6w',
  'cyclone-pump-vsd': 'rs-cyc-pump-vsd-6w',
};

/** Least-squares slope over the full long-window series, threshold ±0.15σ/week. */
export function longTrend(role: string, state: SessionState): LongTrendRead | undefined {
  const seriesId = LONG_SERIES_BY_ROLE[role];
  if (!seriesId) return undefined;
  if (!SERIES_BY_ID[seriesId]) return undefined;
  const points = seriesAt(seriesId, state).points.filter(
    (p): p is { at: SeriesTime; value: number } => p.value !== null,
  );
  if (points.length < 2) return undefined;
  const values = points.map((p) => p.value);
  const xs = values.map((_, i) => i);
  const slopePerPoint = leastSquaresSlope(xs, values);
  const sigma = sampleStdDev(values) || 1;
  const slopeSigmaPerWeek = (slopePerPoint * 7) / sigma;
  let trend: SpcRead['trend'] = 'stable';
  if (slopeSigmaPerWeek > TREND_SIGMA_THRESHOLD) trend = 'rising';
  if (slopeSigmaPerWeek < -TREND_SIGMA_THRESHOLD) trend = 'falling';
  return {
    trend,
    startValue: values[0]!,
    endValue: values[values.length - 1]!,
    slopeSigmaPerWeek,
  };
}

export function excursions(state: SessionState): ExcursionEvent[] {
  const all: ExcursionEvent[] = [];
  for (const variable of MONITORED_VARIABLES) {
    const read = spcRead(variable.id, state);
    all.push(...read.events);
  }
  return all.sort((a, b) => {
    const sev = SEVERITY_RANK[b.severity] - SEVERITY_RANK[a.severity];
    if (sev !== 0) return sev;
    return b.detectedAt.localeCompare(a.detectedAt);
  });
}

const RULE_LABELS: Record<ExcursionRuleKind, string> = {
  'beyond-limit': 'beyond limit',
  'six-point-trend': 'six-point trend',
  'widening-variability': 'widening variability',
  cyclical: 'cyclical',
};

function buildCaption(
  read: SpcRead,
  reading: Reading | undefined,
  spec: Band | undefined,
  primaryEvent?: ExcursionEvent,
  captionOptions?: ControlChartCaptionOptions,
): ControlChartCaption | undefined {
  if (!read.limits || !reading) return undefined;
  const { mean, ucl, lcl, sigma, sigmaSource } = read.limits;
  const caption: ControlChartCaption = {
    mean: formatSpcValue(reading, mean),
    ucl: formatSpcValue(reading, ucl),
    lcl: formatSpcValue(reading, lcl),
    sigma: formatSpcValue(reading, sigma),
  };
  if (primaryEvent) {
    caption.rule = RULE_LABELS[primaryEvent.rule];
    if (primaryEvent.conforming !== undefined) {
      caption.conforming = primaryEvent.conforming ? 'conforming' : 'non-conforming';
    }
  }
  const includeCpk = captionOptions?.includeCpk !== false;
  const showSpecBasis = captionOptions?.showSpecBasis === true;
  if (showSpecBasis && spec) {
    caption.specBound = formatBand(spec, reading.unit, { decimals: spcDisplayDecimals(reading) });
    caption.sigmaSource = sigmaSource;
  } else if (includeCpk && read.capability?.cpk !== undefined) {
    caption.cpk = formatCpkValue(read.capability.cpk);
  }
  return caption;
}

export function controlChart(
  variableId: string,
  state: SessionState,
  captionOptions?: ControlChartCaptionOptions,
): ControlChartViewModel {
  const read = spcRead(variableId, state);
  const reading = READING_BY_ID.get(read.variable.readingId);
  const spec = specForVariable(read.variable);
  const primaryEvent = read.events[0];
  const highlightStamps = new Set(primaryEvent?.points ?? []);
  const points = read.points.map((p) => ({
    ...p,
    highlighted: highlightStamps.has(p.at),
  }));

  if (read.phase === 'learning') {
    const n = read.points.length;
    const N = read.variable.learningPoints;
    return {
      variableId,
      readingId: read.variable.readingId,
      readingLabel: reading?.label ?? variableId,
      phase: 'learning',
      points,
      events: read.events,
      trend: read.trend,
      learningCaption: `Learning · ${n} / ${N} points`,
    };
  }

  return {
    variableId,
    readingId: read.variable.readingId,
    readingLabel: reading?.label ?? variableId,
    phase: read.phase,
    points,
    limits: read.limits,
    spec,
    specLabel: spec ? 'spec' : undefined,
    events: read.events,
    trend: read.trend,
    capability: read.capability,
    signal: read.signal,
    caption: buildCaption(read, reading, spec, primaryEvent, captionOptions),
  };
}

export { MONITORED_VARIABLES };

// --- route-reads samples (paul-call.A4 #253) ---------------------------------
import type { InBandDivergence } from '@/versions/v1/schema/spc';
import { readingComparisonBasis } from '@/versions/v1/shared/comparison-basis';
import { TAG_MAPPING } from '@/versions/v1/shared/bunker-hill-spc';
import { SAMPLE_POINTS } from '@/versions/v1/shared/bunker-hill-seed';

const SAMPLE_POINT_BY_ROLE: Record<string, string> = {};
SAMPLE_POINT_BY_ROLE['zn-rougher-con-zn'] = 'SA08';

export function inBandDivergences(state: SessionState): InBandDivergence[] {
  const out: InBandDivergence[] = [];
  for (const variable of MONITORED_VARIABLES) {
    const read = spcRead(variable.id, state);
    const trendEvent = read.events.find(
      (event) => event.rule === 'six-point-trend' && event.direction,
    );
    if (!trendEvent || !trendEvent.direction) continue;
    const reading = READING_BY_ID.get(variable.readingId);
    if (!reading || reading.band?.lo === undefined || reading.band?.hi === undefined) continue;
    const points = numericPoints(variable, state);
    const byStamp = new Map(points.map((p) => [p.at, p.value]));
    const runValues = trendEvent.points.map((at) => byStamp.get(at));
    if (runValues.some((v) => v === undefined)) continue;
    const lo = reading.band.lo;
    const hi = reading.band.hi;
    const allInBand = runValues.every((v) => (v as number) > lo && (v as number) < hi);
    if (!allInBand) continue;
    const basisRead = readingComparisonBasis(reading);
    if (!basisRead) continue;
    const latestValue = runValues[runValues.length - 1] as number;
    const deltaFromBasis = latestValue - basisRead.value;
    const movingAway = trendEvent.direction === 'down' ? deltaFromBasis < 0 : deltaFromBasis > 0;
    if (!movingAway) continue;
    const tag = TAG_MAPPING.find((row) => row.readingId === variable.readingId);
    const samplePointId = tag ? SAMPLE_POINT_BY_ROLE[tag.role] : undefined;
    out.push({
      id: `div-${variable.id}`,
      excursionId: trendEvent.id,
      variableId: variable.id,
      readingId: variable.readingId,
      subprocessId: tag?.subprocessId ?? '',
      direction: trendEvent.direction,
      detectedAt: trendEvent.detectedAt,
      pointCount: trendEvent.points.length,
      latestValue,
      basisValue: basisRead.value,
      basisLabel: basisRead.label,
      deltaFromBasis,
      samplePointId,
    });
  }
  return out;
}

export function samplePointForDivergence(divergence: InBandDivergence) {
  return SAMPLE_POINTS.find((p) => p.id === divergence.samplePointId);
}
